import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import {
  Users,
  DollarSign,
  CalendarDays,
  Mail,
  Music2,
  Plus,
  Trash2,
  LogOut,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import DocumentsVault from "@/components/DocumentsVault";
import { toast } from "sonner";

const TYPES = ["mechanical", "performance", "sync", "streaming"];
const STATUSES = ["pending", "received", "paid_out"];
const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled"];

function fmt(n) {
  return (
    "$" +
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [artists, setArtists] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);

  const refreshAll = async () => {
    try {
      const [s, a, b, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/artists"),
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setArtists(a.data);
      setBookings(b.data);
      setContacts(c.data);
    } catch (e) {
      console.error("Admin dashboard refresh failed", e);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={TIDS.adminDashboard}>
      <div className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-2">
              / admin control room
            </div>
            <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter">
              E-Quantum Admin
            </h1>
            <p className="text-zinc-500 text-sm mt-2">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <AddRoyaltyDialog artists={artists} onCreated={refreshAll} />
            <AddPlacementDialog artists={artists} onCreated={refreshAll} />
            <button onClick={handleLogout} className="btn-ghost">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10 space-y-8">
        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10">
          {[
            { label: "Artists", v: stats?.artists ?? "—", icon: Users },
            { label: "Bookings", v: stats?.bookings ?? "—", icon: CalendarDays },
            { label: "Contacts", v: stats?.contacts ?? "—", icon: Mail },
            { label: "Placements", v: stats?.placements ?? "—", icon: Music2 },
            { label: "Tracked $", v: stats ? fmt(stats.total_royalties) : "—", icon: DollarSign, accent: true },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className={"bg-[#0a0a0a] p-6 " + (k.accent ? "border-l-2 border-l-[var(--primary)]" : "")}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {k.label}
                  </div>
                  <Icon size={14} className={k.accent ? "text-[var(--primary)]" : "text-zinc-600"} strokeWidth={1.5} />
                </div>
                <div className={"font-heading font-black text-2xl md:text-3xl tracking-tighter " + (k.accent ? "text-[var(--primary)]" : "")}>
                  {k.v}
                </div>
              </div>
            );
          })}
        </section>

        <Tabs defaultValue="artists" className="w-full">
          <TabsList className="bg-[#0a0a0a] border border-white/10 rounded-none p-1 inline-flex">
            <TabsTrigger value="artists" className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black font-mono text-xs uppercase tracking-widest">Artists</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black font-mono text-xs uppercase tracking-widest">Bookings</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black font-mono text-xs uppercase tracking-widest">Contacts</TabsTrigger>
            <TabsTrigger value="ai-jobs" className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black font-mono text-xs uppercase tracking-widest">AI Jobs</TabsTrigger>
            <TabsTrigger value="audits" className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black font-mono text-xs uppercase tracking-widest">Audits</TabsTrigger>
            <TabsTrigger value="structure" className="rounded-none data-[state=active]:bg-[var(--primary)] data-[state=active]:text-black font-mono text-xs uppercase tracking-widest">Structure</TabsTrigger>
          </TabsList>

          <TabsContent value="artists" className="mt-6">
            <ArtistsPanel artists={artists} onChanged={refreshAll} />
          </TabsContent>
          <TabsContent value="bookings" className="mt-6">
            <BookingsPanel bookings={bookings} onChanged={refreshAll} />
          </TabsContent>
          <TabsContent value="contacts" className="mt-6">
            <ContactsPanel contacts={contacts} onChanged={refreshAll} />
          </TabsContent>
          <TabsContent value="ai-jobs" className="mt-6">
            <AIJobsPanel />
          </TabsContent>
          <TabsContent value="audits" className="mt-6">
            <AuditsPanel />
          </TabsContent>
          <TabsContent value="structure" className="mt-6">
            <StructurePanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ArtistsPanel({ artists, onChanged }) {
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);

  const open = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/admin/artists/${id}`);
    setDetail(data);
  };

  const close = () => {
    setActiveId(null);
    setDetail(null);
  };

  const deleteRoyalty = async (rid) => {
    try {
      await api.delete(`/admin/royalties/${rid}`);
      toast.success("Royalty entry removed");
      await open(activeId);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deletePlacement = async (pid) => {
    try {
      await api.delete(`/admin/placements/${pid}`);
      toast.success("Placement removed");
      await open(activeId);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <th className="px-6 py-4">Artist</th>
              <th className="py-4">Email</th>
              <th className="py-4">BMI</th>
              <th className="py-4">ASCAP</th>
              <th className="py-4 text-right pr-6">View</th>
            </tr>
          </thead>
          <tbody>
            {artists.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-zinc-600">
                  No artists yet.
                </td>
              </tr>
            )}
            {artists.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-6 py-4">
                  <div className="font-semibold">{a.artist_name || a.name}</div>
                  <div className="text-xs text-zinc-500">{a.name}</div>
                </td>
                <td className="py-4 text-zinc-400 text-xs font-mono">{a.email}</td>
                <td className="py-4 text-xs font-mono text-zinc-300">
                  {a.publishing?.bmi_status || "not_started"}
                </td>
                <td className="py-4 text-xs font-mono text-zinc-300">
                  {a.publishing?.ascap_status || "not_started"}
                </td>
                <td className="py-4 pr-6 text-right">
                  <button
                    onClick={() => open(a.id)}
                    className="text-[var(--primary)] text-xs font-mono uppercase tracking-widest hover:text-white inline-flex items-center gap-1"
                  >
                    Open <ChevronRight size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!activeId} onOpenChange={(v) => !v && close()}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-4xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
              {detail?.artist?.artist_name || detail?.artist?.name}
            </DialogTitle>
            <div className="text-xs text-zinc-500 font-mono">{detail?.artist?.email}</div>
          </DialogHeader>

          {detail && (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
              <PublishingEditor
                artist={detail.artist}
                onSaved={async () => {
                  await open(activeId);
                  onChanged();
                }}
              />

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-3">
                  Royalties · {detail.royalties.length}
                </div>
                <div className="border border-white/10">
                  {detail.royalties.length === 0 && (
                    <div className="p-4 text-sm text-zinc-600">None.</div>
                  )}
                  {detail.royalties.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between p-3 border-b border-white/5 text-sm last:border-0"
                    >
                      <div>
                        <span className="capitalize text-zinc-200">{r.type}</span>{" "}
                        <span className="text-zinc-500">· {r.source} · {r.period}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-[var(--primary)]">{fmt(r.amount)}</span>
                        <button
                          onClick={() => deleteRoyalty(r.id)}
                          className="text-zinc-500 hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-3">
                  Placements · {detail.placements.length}
                </div>
                <div className="border border-white/10">
                  {detail.placements.length === 0 && (
                    <div className="p-4 text-sm text-zinc-600">None.</div>
                  )}
                  {detail.placements.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 border-b border-white/5 text-sm last:border-0">
                      <div>
                        <span className="text-zinc-200">{p.track_title}</span>{" "}
                        <span className="text-zinc-500">· {p.platform}{p.playlist_or_show ? ` · ${p.playlist_or_show}` : ""}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-zinc-400 text-xs">{p.placed_on || "—"}</span>
                        <button onClick={() => deletePlacement(p.id)} className="text-zinc-500 hover:text-red-400">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DocumentsVault
                listUrl={`/admin/artists/${detail.artist.id}/documents`}
                uploadUrl={`/admin/artists/${detail.artist.id}/documents`}
                deleteUrl={(id) => `/me/documents/${id}`}
                downloadUrl={(id) => `/me/documents/${id}/download`}
                heading={`Documents for ${detail.artist.artist_name || detail.artist.name}`}
                canUpload={true}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PublishingEditor({ artist, onSaved }) {
  const [form, setForm] = useState({
    bmi_status: artist?.publishing?.bmi_status || "not_started",
    ascap_status: artist?.publishing?.ascap_status || "not_started",
    bmi_id: artist?.publishing?.bmi_id || "",
    ascap_id: artist?.publishing?.ascap_id || "",
    pro_affiliation: artist?.publishing?.pro_affiliation || "",
    publisher_name: artist?.publishing?.publisher_name || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/artists/${artist.id}/publishing`, form);
      toast.success("Publishing updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-white/10 p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-4">
        Publishing
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest">BMI status</label>
          <select className="input-gold mt-1" value={form.bmi_status} onChange={update("bmi_status")}>
            <option value="not_started">Not started</option>
            <option value="in_progress">In progress</option>
            <option value="registered">Registered</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest">ASCAP status</label>
          <select className="input-gold mt-1" value={form.ascap_status} onChange={update("ascap_status")}>
            <option value="not_started">Not started</option>
            <option value="in_progress">In progress</option>
            <option value="registered">Registered</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest">BMI ID</label>
          <input className="input-gold mt-1" value={form.bmi_id} onChange={update("bmi_id")} />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest">ASCAP ID</label>
          <input className="input-gold mt-1" value={form.ascap_id} onChange={update("ascap_id")} />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest">PRO affiliation</label>
          <input className="input-gold mt-1" value={form.pro_affiliation} onChange={update("pro_affiliation")} />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Publisher name</label>
          <input className="input-gold mt-1" value={form.publisher_name} onChange={update("publisher_name")} />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-publishing">
          {saving ? "Saving…" : "Save publishing"}
        </button>
      </div>
    </div>
  );
}

function AddRoyaltyDialog({ artists, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    artist_id: "",
    type: "streaming",
    source: "",
    period: "",
    amount: "",
    status: "pending",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/royalties", {
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success("Royalty entry added");
      setOpen(false);
      setForm({ artist_id: "", type: "streaming", source: "", period: "", amount: "", status: "pending", notes: "" });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={TIDS.addRoyaltyBtn} className="btn-primary">
          <Plus size={14} /> Royalty
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter">
            Add royalty entry
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Artist</label>
            <select required className="input-gold mt-1" value={form.artist_id} onChange={update("artist_id")} data-testid={TIDS.royaltyArtist}>
              <option value="">Select…</option>
              {artists.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.artist_name || a.name} ({a.email})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Type</label>
              <select className="input-gold mt-1" value={form.type} onChange={update("type")} data-testid={TIDS.royaltyType}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Status</label>
              <select className="input-gold mt-1" value={form.status} onChange={update("status")} data-testid={TIDS.royaltyStatus}>
                {STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Source</label>
              <input required className="input-gold mt-1" value={form.source} onChange={update("source")} placeholder="Spotify, BMI…" data-testid={TIDS.royaltySource}/>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Period</label>
              <input required className="input-gold mt-1" value={form.period} onChange={update("period")} placeholder="Q4 2025" data-testid={TIDS.royaltyPeriod}/>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Amount (USD)</label>
            <input required type="number" step="0.01" className="input-gold mt-1" value={form.amount} onChange={update("amount")} data-testid={TIDS.royaltyAmount}/>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="btn-primary" data-testid={TIDS.royaltySubmit}>
              {saving ? "Saving…" : "Add entry"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddPlacementDialog({ artists, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    artist_id: "",
    platform: "Spotify",
    track_title: "",
    playlist_or_show: "",
    placed_on: "",
  });
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/placements", form);
      toast.success("Placement added");
      setOpen(false);
      setForm({ artist_id: "", platform: "Spotify", track_title: "", playlist_or_show: "", placed_on: "" });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={TIDS.addPlacementBtn} className="btn-ghost">
          <Plus size={14} /> Placement
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter">
            Add placement
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Artist</label>
            <select required className="input-gold mt-1" value={form.artist_id} onChange={update("artist_id")}>
              <option value="">Select…</option>
              {artists.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.artist_name || a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Platform</label>
              <input required className="input-gold mt-1" value={form.platform} onChange={update("platform")} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Placed on</label>
              <input className="input-gold mt-1" type="date" value={form.placed_on} onChange={update("placed_on")} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Track title</label>
            <input required className="input-gold mt-1" value={form.track_title} onChange={update("track_title")} />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest">Playlist / show</label>
            <input className="input-gold mt-1" value={form.playlist_or_show} onChange={update("playlist_or_show")} />
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? "Saving…" : "Add placement"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BookingsPanel({ bookings, onChanged }) {
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking ${status}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <th className="px-6 py-4">When</th>
            <th className="py-4">Artist</th>
            <th className="py-4">Service</th>
            <th className="py-4">Status</th>
            <th className="py-4 text-right pr-6">Actions</th>
          </tr>
        </thead>
        <tbody>
          {bookings.length === 0 && (
            <tr><td colSpan={5} className="px-6 py-10 text-center text-zinc-600">No bookings yet.</td></tr>
          )}
          {bookings.map((b) => (
            <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="px-6 py-4 font-mono text-xs text-zinc-300">{b.date} · {b.time_slot}</td>
              <td className="py-4">
                <div className="text-zinc-200">{b.name}</div>
                <div className="text-xs text-zinc-500">{b.email}</div>
              </td>
              <td className="py-4 text-zinc-300">{b.service}</td>
              <td className="py-4">
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--primary)]">{b.status}</span>
              </td>
              <td className="py-4 pr-6 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger className="text-xs font-mono uppercase tracking-widest text-[var(--primary)] hover:text-white">
                    Update ▾
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none">
                    {BOOKING_STATUSES.map((s) => (
                      <DropdownMenuItem key={s} onClick={() => setStatus(b.id, s)} className="capitalize">
                        {s}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsPanel({ contacts, onChanged }) {
  const markRead = async (id) => {
    try {
      await api.patch(`/admin/contacts/${id}/read`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  return (
    <div className="space-y-3">
      {contacts.length === 0 && (
        <div className="bg-[#0a0a0a] border border-white/10 p-10 text-center text-zinc-600">
          No messages.
        </div>
      )}
      {contacts.map((c) => (
        <div key={c.id} className={"bg-[#0a0a0a] border p-6 " + (c.read ? "border-white/5" : "border-[var(--primary)]/30")}>
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-zinc-500 font-mono">{c.email} · {c.subject}</div>
            </div>
            {!c.read && (
              <button onClick={() => markRead(c.id)} className="text-xs font-mono uppercase tracking-widest text-[var(--primary)] hover:text-white">
                Mark read
              </button>
            )}
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{c.message}</p>
        </div>
      ))}
    </div>
  );
}


function AIJobsPanel() {
  const [jobs, setJobs] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [active, setActive] = React.useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [j, s] = await Promise.all([
        api.get(`/admin/ai-jobs${statusFilter ? `?status=${statusFilter}` : ""}`),
        api.get(`/admin/ai-jobs/stats`),
      ]);
      setJobs(j.data);
      setStats(s.data);
    } catch (e) {
      console.error("Failed to load AI jobs", e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const STATUS_COLORS = {
    completed: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    failed: "text-red-300 border-red-500/40 bg-red-500/10",
    pending: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  };
  const fmtUsd = (n) =>
    "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6" data-testid="ai-jobs-panel">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-white/10">
        {[
          { k: "Total", v: stats?.total ?? "—" },
          { k: "Completed", v: stats?.completed ?? "—" },
          { k: "Failed", v: stats?.failed ?? "—" },
          { k: "Pending", v: stats?.pending ?? "—" },
          {
            k: "Cost 24h",
            v: stats ? fmtUsd(stats.estimated_cost_24h_usd) : "—",
            accent: true,
          },
          {
            k: "Cost total",
            v: stats ? fmtUsd(stats.estimated_cost_total_usd) : "—",
            accent: true,
          },
        ].map((s) => (
          <div
            key={s.k}
            className={"bg-[#0a0a0a] p-5 " + (s.accent ? "border-l-2 border-l-[var(--primary)]" : "")}
            data-testid={`ai-stat-${s.k.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</div>
            <div className={"font-heading font-black text-2xl tracking-tighter mt-1 " + (s.accent ? "text-[var(--primary)]" : "")}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Filter</span>
        {[
          ["", "All"],
          ["completed", "Completed"],
          ["failed", "Failed"],
          ["pending", "Pending"],
        ].map(([id, label]) => (
          <button
            key={id || "all"}
            onClick={() => setStatusFilter(id)}
            data-testid={`ai-filter-${id || "all"}`}
            className={
              "px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors " +
              (statusFilter === id
                ? "bg-[var(--primary)] text-black border-[var(--primary)]"
                : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30")
            }
          >
            {label}
          </button>
        ))}
        {stats && (
          <span className="ml-auto font-mono text-[10px] text-zinc-600">
            // rates: ${stats.rates_usd_per_sec["sora-2"]}/s · pro ${stats.rates_usd_per_sec["sora-2-pro"]}/s
          </span>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <th className="px-6 py-4">When</th>
              <th className="py-4">Artist</th>
              <th className="py-4">Style</th>
              <th className="py-4">Model</th>
              <th className="py-4">Dur.</th>
              <th className="py-4">Status</th>
              <th className="py-4 text-right pr-6">Cost</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-zinc-600">Loading…</td></tr>
            )}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-zinc-600">No jobs match this filter.</td></tr>
            )}
            {jobs.map((j) => (
              <tr
                key={j.id}
                className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                onClick={() => setActive(j)}
                data-testid={`ai-job-row-${j.id}`}
              >
                <td className="px-6 py-4 font-mono text-[10px] text-zinc-500">
                  {(j.created_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td className="py-4 text-zinc-300 text-xs">{j.email}</td>
                <td className="py-4 capitalize text-zinc-300">{j.style || "—"}</td>
                <td className="py-4 font-mono text-xs">
                  {j.model || "—"}
                  {j.quality === "pro" && (
                    <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-[var(--primary)]/10 text-[var(--primary)] border border-[var(--primary)]/30">PRO</span>
                  )}
                </td>
                <td className="py-4 font-mono text-xs text-zinc-300">{j.duration ? `${j.duration}s` : "—"}</td>
                <td className="py-4">
                  <span
                    className={
                      "inline-flex px-2 py-1 text-[10px] font-mono uppercase tracking-widest border " +
                      (STATUS_COLORS[j.status] || "text-zinc-500 border-white/10")
                    }
                  >
                    {j.status}
                  </span>
                </td>
                <td className="py-4 pr-6 text-right font-mono text-[var(--primary)]">
                  {fmtUsd(j.estimated_cost_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
              Sora 2 job
            </DialogTitle>
            <div className="text-xs text-zinc-500 font-mono">{active?.id}</div>
          </DialogHeader>
          {active && (
            <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ["Status", active.status],
                  ["Artist", active.email],
                  ["Style", active.style],
                  ["Quality", active.quality],
                  ["Model", active.model || "—"],
                  ["Duration", active.duration ? `${active.duration}s` : "—"],
                  ["Size", active.size || "—"],
                  ["Estimated cost", fmtUsd(active.estimated_cost_usd)],
                  ["Track title", active.track_title || "—"],
                  ["Used top-up", active.used_topup ? "Yes" : "No"],
                  ["Created", (active.created_at || "").replace("T", " ").slice(0, 19)],
                  ["Completed", (active.completed_at || "—").toString().replace("T", " ").slice(0, 19)],
                ].map(([k, v]) => (
                  <div key={k} className="border border-white/10 p-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{k}</div>
                    <div className="text-zinc-200 capitalize text-sm break-words">{v || "—"}</div>
                  </div>
                ))}
              </div>

              {active.extra_prompt && (
                <div className="border border-white/10 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Extra direction
                  </div>
                  <div className="text-zinc-300 text-sm">{active.extra_prompt}</div>
                </div>
              )}

              <div className="border border-white/10 p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  Full prompt
                </div>
                <pre className="text-zinc-300 text-xs whitespace-pre-wrap font-mono leading-relaxed">{active.prompt}</pre>
              </div>

              {active.status === "failed" && active.error && (
                <div className="border border-red-500/40 bg-red-500/5 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-300 mb-2">
                    Failure reason
                  </div>
                  <div className="text-red-200 text-sm">{active.error}</div>
                </div>
              )}

              {active.status === "completed" && active.video_url && (
                <div className="space-y-3">
                  <video
                    src={`${process.env.REACT_APP_BACKEND_URL}${active.video_url}`}
                    controls
                    playsInline
                    className="w-full max-h-[420px] bg-black border border-white/10"
                  />
                  {active.share_path && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500 font-mono">Public share:</span>
                      <a
                        href={active.share_path}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--primary)] hover:underline font-mono break-all"
                      >
                        {window.location.origin}{active.share_path}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const AUDIT_TIER_STYLES = {
  quantum_ready: { color: "text-[#d4af37]", border: "border-[#d4af37]/50 bg-[#d4af37]/10", icon: "⚡" },
  strong_foundation: { color: "text-emerald-300", border: "border-emerald-500/40 bg-emerald-500/10", icon: "◆" },
  revenue_leaks: { color: "text-amber-300", border: "border-amber-500/40 bg-amber-500/10", icon: "⚠" },
  critical_missing: { color: "text-red-300", border: "border-red-500/40 bg-red-500/10", icon: "▲" },
  emergency: { color: "text-rose-300", border: "border-rose-500/50 bg-rose-500/10", icon: "!" },
};

function AuditsPanel() {
  const [audits, setAudits] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState("");
  const [tierFilter, setTierFilter] = React.useState("all");
  const [active, setActive] = React.useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audits?limit=500");
      setAudits(data);
    } catch (e) {
      console.error("Failed to load audits", e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    refresh();
  }, []);

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return audits.filter((a) => {
      if (tierFilter !== "all" && a.tier_id !== tierFilter) return false;
      if (!needle) return true;
      return (
        (a.email || "").toLowerCase().includes(needle) ||
        (a.answers?.artist_name || "").toLowerCase().includes(needle) ||
        (a.answers?.stage_name || "").toLowerCase().includes(needle)
      );
    });
  }, [audits, q, tierFilter]);

  const stats = React.useMemo(() => {
    const buckets = { quantum_ready: 0, strong_foundation: 0, revenue_leaks: 0, critical_missing: 0, emergency: 0 };
    const hotLeads = [];
    let sum = 0;
    audits.forEach((a) => {
      buckets[a.tier_id] = (buckets[a.tier_id] || 0) + 1;
      sum += Number(a.score || 0);
      if (a.answers?.interest_level === "yes") hotLeads.push(a);
    });
    return {
      total: audits.length,
      avg: audits.length ? Math.round(sum / audits.length) : 0,
      hot: hotLeads.length,
      ...buckets,
    };
  }, [audits]);

  return (
    <div className="space-y-6" data-testid="audits-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
        {[
          { k: "Total audits", v: stats.total, accent: false },
          { k: "Avg score", v: stats.avg, accent: true },
          { k: "Hot leads", v: stats.hot, accent: true },
          { k: "Emergency tier", v: stats.emergency + stats.critical_missing, accent: false },
        ].map((s) => (
          <div key={s.k} className={"bg-[#0a0a0a] p-5 " + (s.accent ? "border-l-2 border-l-[var(--primary)]" : "")}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{s.k}</div>
            <div className={"font-heading font-black text-2xl tracking-tighter mt-1 " + (s.accent ? "text-[var(--primary)]" : "")}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          data-testid="audits-search-input"
          className="bg-transparent border border-white/10 focus:border-[var(--primary)] outline-none px-3 py-2 text-sm text-white placeholder-zinc-600 flex-1 min-w-[220px]"
        />
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          data-testid="audits-tier-filter"
          className="bg-[#0a0a0a] border border-white/10 focus:border-[var(--primary)] outline-none px-3 py-2 text-sm text-white"
        >
          <option value="all">All tiers</option>
          <option value="quantum_ready">Quantum Ready (90+)</option>
          <option value="strong_foundation">Strong Foundation (70-89)</option>
          <option value="revenue_leaks">Revenue Leaks (50-69)</option>
          <option value="critical_missing">Critical Missing (30-49)</option>
          <option value="emergency">Emergency (0-29)</option>
        </select>
        <button onClick={refresh} className="btn-ghost text-sm" data-testid="audits-refresh">
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 bg-[#0a0a0a] font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="col-span-3">Artist / Email</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-3">Tier</div>
          <div className="col-span-2">Interest</div>
          <div className="col-span-2 text-right">Submitted</div>
        </div>
        {loading && (
          <div className="p-6 text-center text-zinc-500 text-sm font-mono">Loading audits…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-zinc-500 text-sm font-mono">
            {audits.length === 0 ? "// No audits submitted yet." : "// No matches for these filters."}
          </div>
        )}
        {!loading && filtered.map((a) => {
          const tier = AUDIT_TIER_STYLES[a.tier_id] || AUDIT_TIER_STYLES.strong_foundation;
          return (
            <button
              key={a.id}
              onClick={() => setActive(a)}
              data-testid={`audit-row-${a.id}`}
              className="w-full text-left grid grid-cols-12 gap-4 px-4 py-4 border-b border-white/5 hover:bg-white/5 transition-colors"
            >
              <div className="col-span-3 min-w-0">
                <div className="text-sm text-white truncate font-medium">
                  {a.answers?.stage_name || a.answers?.artist_name || "—"}
                </div>
                <div className="text-xs text-zinc-500 truncate">{a.email}</div>
              </div>
              <div className={"col-span-2 font-heading font-black text-2xl tracking-tighter " + tier.color}>
                {a.score}
              </div>
              <div className="col-span-3">
                <span className={"font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-1 border " + tier.border + " " + tier.color}>
                  {tier.icon} {a.tier_label}
                </span>
              </div>
              <div className="col-span-2 text-sm">
                {a.answers?.interest_level === "yes" && (
                  <span className="text-[var(--primary)] font-mono text-[10px] tracking-widest">HOT LEAD</span>
                )}
                {a.answers?.interest_level === "maybe" && (
                  <span className="text-zinc-400 font-mono text-[10px] tracking-widest">Maybe</span>
                )}
                {a.answers?.interest_level === "not_now" && (
                  <span className="text-zinc-600 font-mono text-[10px] tracking-widest">Not now</span>
                )}
              </div>
              <div className="col-span-2 text-right text-xs text-zinc-500 font-mono">
                {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#050505] border border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          {active && (
            <div>
              <DialogHeader>
                <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
                  {active.answers?.stage_name || active.answers?.artist_name || "Audit submission"}
                </DialogTitle>
              </DialogHeader>

              {/* Score header */}
              <div className={"border p-6 mt-4 text-center " + (AUDIT_TIER_STYLES[active.tier_id]?.border || "")}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Score</div>
                <div className={"font-heading font-black text-6xl tracking-tighter leading-none " + (AUDIT_TIER_STYLES[active.tier_id]?.color || "text-white")}>
                  {active.score}
                </div>
                <div className="text-lg font-heading font-black mt-3">
                  {active.tier_label} <span>{AUDIT_TIER_STYLES[active.tier_id]?.icon}</span>
                </div>
              </div>

              {/* Contact */}
              <div className="mt-6 space-y-1.5 text-sm">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// contact</div>
                <div><span className="text-zinc-500">Artist:</span> <span className="text-white">{active.answers?.artist_name || "—"}</span></div>
                {active.answers?.stage_name && <div><span className="text-zinc-500">Stage:</span> <span className="text-white">{active.answers.stage_name}</span></div>}
                <div><span className="text-zinc-500">Email:</span> <a href={`mailto:${active.email}`} className="text-[var(--primary)] hover:underline">{active.email}</a></div>
                <div><span className="text-zinc-500">Phone:</span> <a href={`tel:${active.answers?.phone}`} className="text-white">{active.answers?.phone || "—"}</a></div>
                {active.answers?.instagram && <div><span className="text-zinc-500">IG:</span> <span className="text-white">{active.answers.instagram}</span></div>}
                {active.answers?.tiktok && <div><span className="text-zinc-500">TikTok:</span> <span className="text-white">{active.answers.tiktok}</span></div>}
                {active.answers?.website && <div><span className="text-zinc-500">Web:</span> <a href={active.answers.website} target="_blank" rel="noreferrer" className="text-[var(--primary)] hover:underline break-all">{active.answers.website}</a></div>}
              </div>

              {/* Breakdown */}
              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// score breakdown</div>
                <div className="border border-white/10">
                  {Object.entries(active.breakdown || {}).map(([k, v], i) => (
                    <div key={k} className={"flex items-center justify-between px-3 py-2 text-sm " + (i > 0 ? "border-t border-white/5" : "")}>
                      <span className="text-zinc-300">{k}</span>
                      <span className={"font-mono " + (v === 0 ? "text-red-400" : "text-white")}>{v} pts</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action items */}
              {active.action_items?.length > 0 && (
                <div className="mt-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// action items</div>
                  <ul className="space-y-2">
                    {active.action_items.map((t) => (
                      <li key={t} className="text-sm text-zinc-200 leading-relaxed flex gap-2">
                        <span className="text-[var(--primary)]">→</span> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Signals */}
              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// signals</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-500">Interest:</span> <span className="text-white">{active.answers?.interest_level || "—"}</span></div>
                  <div><span className="text-zinc-500">Confident (Tokyo):</span> <span className="text-white">{active.answers?.confident_about_japan || "—"}</span></div>
                  <div><span className="text-zinc-500">Monthly listeners:</span> <span className="text-white">{Number(active.answers?.monthly_listeners || 0).toLocaleString()}</span></div>
                  <div><span className="text-zinc-500">IG followers:</span> <span className="text-white">{Number(active.answers?.instagram_followers || 0).toLocaleString()}</span></div>
                  <div><span className="text-zinc-500">TikTok followers:</span> <span className="text-white">{Number(active.answers?.tiktok_followers || 0).toLocaleString()}</span></div>
                  <div><span className="text-zinc-500">Songs released:</span> <span className="text-white">{active.answers?.songs_released || 0}</span></div>
                </div>
              </div>

              {/* Goals */}
              {active.answers?.goals_12mo && (
                <div className="mt-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// 12-month goal</div>
                  <p className="text-sm text-zinc-200 leading-relaxed italic border-l-2 border-[var(--primary)] pl-4">
                    &ldquo;{active.answers.goals_12mo}&rdquo;
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex gap-2 flex-wrap">
                <a
                  href={`mailto:${active.email}?subject=${encodeURIComponent(`Your E-Quantum Score: ${active.score} — Let's talk`)}`}
                  className="btn-primary text-sm"
                >
                  Email artist
                </a>
                <a
                  href={`/booking?email=${encodeURIComponent(active.email)}&audit_score=${active.score}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost text-sm"
                >
                  Open booking pre-fill
                </a>
                <button onClick={() => setActive(null)} className="btn-ghost text-sm">Close</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------------------------------------------------- */
/*   STRUCTURE ONBOARDING PANEL (admin)                        */
/* ---------------------------------------------------------- */

const STRUCTURE_STATUSES = [
  { v: "not_needed", l: "Not needed" },
  { v: "pending",    l: "Pending intake" },
  { v: "ready",      l: "Ready to file" },
  { v: "submitted",  l: "Submitted" },
  { v: "approved",   l: "Approved" },
  { v: "active",     l: "Active" },
  { v: "blocked",    l: "Blocked" },
];

function StructurePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full intake doc when clicked

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/structure/queue");
      setRows(data);
    } catch (e) {
      toast.error("Could not load Structure queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openIntake = async (id) => {
    try {
      const { data } = await api.get(`/admin/structure/${id}`);
      setSelected(data);
    } catch (e) {
      toast.error("Could not load intake.");
    }
  };

  const updateService = async (svcKey, patch) => {
    try {
      await api.patch(`/admin/structure/${selected.id}/service/${svcKey}`, patch);
      toast.success(`${svcKey}: ${patch.status}`);
      // Reload the intake to reflect
      const { data } = await api.get(`/admin/structure/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };

  if (loading) {
    return <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest p-6">Loading queue…</div>;
  }

  if (selected) {
    return <StructureIntakeDetail intake={selected} onClose={() => setSelected(null)} onUpdate={updateService} />;
  }

  return (
    <div className="bg-[#0a0a0a] border border-white/10" data-testid="admin-structure-queue">
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// Structure onboarding queue</div>
          <h3 className="font-heading font-black text-2xl tracking-tight">{rows.length} intakes</h3>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm">No intakes yet. Clients will appear here after they complete onboarding.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left p-4">Rank</th>
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Email</th>
                <th className="text-center p-4">Score</th>
                <th className="text-center p-4">Selected</th>
                <th className="text-center p-4">Active</th>
                <th className="text-center p-4">In progress</th>
                <th className="text-center p-4">Ready</th>
                <th className="text-left p-4">Submitted</th>
                <th className="text-right p-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`intake-row-${r.id}`}>
                  <td className="p-4 font-mono text-zinc-500 text-xs">#{i + 1}</td>
                  <td className="p-4 text-white font-medium">{r.name || "—"}<div className="text-zinc-500 text-xs">{r.stage_name}</div></td>
                  <td className="p-4 text-zinc-400">{r.email}</td>
                  <td className="p-4 text-center">
                    <div className="inline-flex items-center gap-2">
                      <span className="font-heading font-black text-lg text-[var(--primary)]">{r.success_score}</span>
                      <span className="font-mono text-[9px] tracking-widest uppercase text-zinc-500">/{r.success_grade}</span>
                    </div>
                  </td>
                  <td className="p-4 text-center font-mono">{r.target_count}</td>
                  <td className="p-4 text-center font-mono text-[#22c55e]">{r.counts.active}</td>
                  <td className="p-4 text-center font-mono text-[#3b82f6]">{r.counts.submitted}</td>
                  <td className="p-4 text-center font-mono text-zinc-400">{r.counts.ready}</td>
                  <td className="p-4 text-zinc-500 text-xs font-mono">{r.created_at?.slice(0,10) || "—"}</td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => openIntake(r.id)}
                      className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] hover:text-white"
                      data-testid={`open-intake-${r.id}`}
                    >
                      Open &rarr;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StructureIntakeDetail({ intake, onClose, onUpdate }) {
  const manifest = (intake.manifest || []).filter((m) => m.status !== "not_needed");

  return (
    <div className="space-y-6" data-testid="intake-detail">
      <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white">
        &larr; Back to queue
      </button>

      <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// intake</div>
        <h3 className="font-heading font-black text-3xl tracking-tight mb-6">
          {intake.legal_first_name} {intake.legal_last_name}
          {intake.stage_name && <span className="text-zinc-500 text-lg ml-3">· {intake.stage_name}</span>}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <IntakeField label="Email" value={intake.email} />
          <IntakeField label="Phone" value={intake.phone} />
          <IntakeField label="DOB" value={intake.dob} />
          <IntakeField label="Address" value={`${intake.address_line1}${intake.address_line2 ? ", " + intake.address_line2 : ""}, ${intake.city}, ${intake.state_region} ${intake.postal_code}`} />
          <IntakeField label="Country" value={intake.country} />
          <IntakeField label="SSN/EIN" value={intake.ssn_or_ein} />
          <IntakeField label="LLC" value={intake.has_llc + (intake.llc_name ? ` (${intake.llc_name})` : "")} />
          <IntakeField label="EIN" value={intake.ein || "—"} />
          <IntakeField label="Publisher Entity" value={intake.publisher_entity_name || "Create for me"} />
          <IntakeField label="Preferred PRO" value={intake.primary_pro_preference} />
          <IntakeField label="Catalog size" value={intake.catalog_size} />
          <IntakeField label="Distributor" value={intake.distributor || "—"} />
          <IntakeField label="Bank" value={intake.bank_name || "—"} />
          <IntakeField label="Routing ••" value={intake.bank_routing_last4 || "—"} />
          <IntakeField label="Account ••" value={intake.bank_account_last4 || "—"} />
          <IntakeField label="Signed by" value={intake.signature} />
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10">
        <div className="p-6 border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// service manifest</div>
          <h4 className="font-heading font-bold text-xl">{manifest.length} services to file</h4>
        </div>
        <div className="divide-y divide-white/5">
          {manifest.map((row) => (
            <StructureRow key={row.key} row={row} onUpdate={onUpdate} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StructureRow({ row, onUpdate }) {
  const [status, setStatus] = useState(row.status);
  const [notes, setNotes] = useState(row.notes || "");
  const [ref, setRef] = useState(row.reference_id || "");
  const [saving, setSaving] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);

  const save = async () => {
    setSaving(true);
    await onUpdate(row.key, { status, notes, reference_id: ref });
    setSaving(false);
  };

  return (
    <div>
      <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-3">
          <div className="text-white font-medium text-sm flex items-center gap-2">
            {row.name}
            {row.playbook && (
              <button
                type="button"
                onClick={() => setShowPlaybook((v) => !v)}
                className="font-mono text-[9px] uppercase tracking-widest text-[var(--primary)]/70 hover:text-[var(--primary)] border border-[var(--primary)]/30 px-1.5 py-0.5"
                data-testid={`playbook-toggle-${row.key}`}
              >
                {showPlaybook ? "hide" : "playbook"}
              </button>
            )}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{row.category}</div>
        </div>
        <div className="md:col-span-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full bg-black border border-white/15 text-white text-xs px-3 py-2 font-mono uppercase tracking-widest"
            data-testid={`status-${row.key}`}
          >
            {STRUCTURE_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
        <div className="md:col-span-3">
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="Reference ID (IPI, member #)"
            className="w-full bg-transparent border-b border-white/15 focus:border-[var(--primary)] outline-none text-white text-xs py-2 px-1"
            data-testid={`ref-${row.key}`}
          />
        </div>
        <div className="md:col-span-3">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes"
            className="w-full bg-transparent border-b border-white/15 focus:border-[var(--primary)] outline-none text-white text-xs py-2 px-1"
            data-testid={`notes-${row.key}`}
          />
        </div>
        <div className="md:col-span-1 text-right">
          <button
            onClick={save}
            disabled={saving}
            className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] hover:text-white disabled:opacity-40"
            data-testid={`save-${row.key}`}
          >
            {saving ? "…" : "Save"}
          </button>
        </div>
      </div>
      {showPlaybook && row.playbook !== undefined && (
        <div className="px-4 md:px-6 pb-5 pt-1" data-testid={`playbook-body-${row.key}`}>
          <PlaybookEditor serviceKey={row.key} initial={row.playbook || ""} />
        </div>
      )}
    </div>
  );
}

function PlaybookEditor({ serviceKey, initial }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/structure/playbook/${serviceKey}`, { playbook: text });
      toast.success("Playbook updated.");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this playbook back to the default? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.post(`/admin/structure/playbook/${serviceKey}/reset`);
      toast.success("Reset to default. Reopen the intake to see the change.");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-l-2 border-[var(--primary)]/50 bg-[var(--primary)]/[0.03] px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[9px] tracking-widest uppercase text-[var(--primary)]">// Registration playbook</div>
        <div className="flex gap-3">
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="font-mono text-[9px] uppercase tracking-widest text-[var(--primary)] hover:text-white disabled:opacity-40" data-testid={`playbook-save-${serviceKey}`}>
                {saving ? "…" : "Save"}
              </button>
              <button onClick={() => { setText(initial); setEditing(false); }} className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white">
                Cancel
              </button>
              <button onClick={reset} disabled={saving} className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-red-400" data-testid={`playbook-reset-${serviceKey}`}>
                Reset
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="font-mono text-[9px] uppercase tracking-widest text-[var(--primary)] hover:text-white" data-testid={`playbook-edit-${serviceKey}`}>
              Edit
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          className="w-full bg-black border border-white/15 focus:border-[var(--primary)] outline-none p-3 text-zinc-100 text-xs font-mono leading-relaxed"
          data-testid={`playbook-textarea-${serviceKey}`}
        />
      ) : (
        <p className="text-zinc-300 text-xs leading-relaxed whitespace-pre-line">{text}</p>
      )}
    </div>
  );
}

function IntakeField({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-white text-sm break-words">{value || "—"}</div>
    </div>
  );
}

